import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Calendar, Clock, Phone, Mail, User, XCircle, CalendarClock, Loader2, CheckCircle, MapPin, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatIt(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}
function todayIso() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}

export default function ClientRilievoPage() {
  const { magicLink } = useParams();
  const [loading, setLoading] = useState(true);
  const [r, setR] = useState(null);
  const [error, setError] = useState(null);

  const [showCancella, setShowCancella] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const [showRipro, setShowRipro] = useState(false);
  const [newDate, setNewDate] = useState(todayIso());
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  const fetchR = async () => {
    try {
      const res = await axios.get(`${API}/client/rilievo/${magicLink}`);
      setR(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Rilievo non trovato");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchR(); /* eslint-disable-next-line */ }, [magicLink]);

  const fetchSlots = async (d) => {
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/client/rilievo/${magicLink}/slots?data_str=${d}`);
      if (res.data?.closed) setSlots([]);
      else setSlots((res.data?.slots || []).filter((s) => s.libero));
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  useEffect(() => { if (showRipro && newDate) fetchSlots(newDate); /* eslint-disable-next-line */ }, [showRipro, newDate]);

  const handleCancella = async () => {
    if (motivo.trim().length < 3) { toast.error("Inserisci un motivo (min 3 caratteri)"); return; }
    setCancelling(true);
    try {
      await axios.post(`${API}/client/rilievo/${magicLink}/cancella`, { motivo: motivo.trim() });
      toast.success("Rilievo cancellato. Ti ricontatteremo per riprogrammare.");
      setShowCancella(false); fetchR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setCancelling(false); }
  };

  const handleRiprogramma = async () => {
    if (!selectedSlot) { toast.error("Seleziona un orario"); return; }
    setRescheduling(true);
    try {
      await axios.post(`${API}/client/rilievo/${magicLink}/riprogramma`, { data: newDate, orario: selectedSlot });
      toast.success("Rilievo riprogrammato!");
      setShowRipro(false); setSelectedSlot(""); fetchR();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally { setRescheduling(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <XCircle className="w-14 h-14 text-red-500 mx-auto mb-3" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Rilievo non trovato</h1>
        <p className="text-slate-600">{error}</p>
      </div>
    </div>
  );

  const isCancellato = r?.workflow_stato === "da_programmare" && r?.cancellazione_motivo;
  const isClosed = r?.workflow_stato === "chiuso";

  // Blocco 24h: non si può modificare se mancano meno di 24h all'appuntamento
  const hoursUntilAppt = (() => {
    if (!r?.data || !r?.orario) return null;
    try {
      const dt = new Date(`${r.data}T${r.orario}:00`);
      return (dt.getTime() - Date.now()) / (1000 * 60 * 60);
    } catch { return null; }
  })();
  const within24h = hoursUntilAppt !== null && hoursUntilAppt < 24 && hoursUntilAppt > 0;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <Toaster position="top-right" richColors />
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
            <h1 className="text-2xl font-bold">Il tuo Rilievo Tecnico</h1>
            <p className="text-blue-100 mt-1">Showroom Ingenio · 070 204 3952</p>
          </div>

          <div className="p-6 space-y-4">
            {isCancellato && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="font-semibold text-red-800">Rilievo cancellato</p>
                <p className="text-sm text-red-700 mt-1">Motivo: {r.cancellazione_motivo}</p>
              </div>
            )}
            {isClosed && (
              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <p className="font-semibold text-emerald-800">Rilievo completato</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <InfoRow icon={User} label="Cliente" value={`${r.cognome} ${r.nome}`} />
              {r.telefono && <InfoRow icon={Phone} label="Telefono" value={r.telefono} />}
              {r.email && <InfoRow icon={Mail} label="Email" value={r.email} />}
              {r.data && <InfoRow icon={Calendar} label="Data" value={formatIt(r.data)} />}
              {r.orario && <InfoRow icon={Clock} label="Orario" value={r.orario} />}
              {r.motivo && <InfoRow icon={FileText} label="Motivo" value={r.motivo} />}
              {r.indirizzo && <InfoRow icon={MapPin} label="Indirizzo" value={r.indirizzo} />}
            </div>

            {!isCancellato && !isClosed && within24h && (
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                <p className="font-semibold text-amber-900">⚠️ Modifica non disponibile</p>
                <p className="text-sm text-amber-800 mt-1">
                  Mancano meno di 24 ore all'appuntamento. Per riprogrammare o cancellare contattaci al <strong>070 204 3952</strong>.
                </p>
              </div>
            )}

            {!isCancellato && !isClosed && !within24h && (
              <div className="pt-4 border-t grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  type="button"
                  data-state="colored"
                  style={{ backgroundColor: "#2563eb" }}
                  onClick={() => setShowRipro(true)}
                  className="text-white hover:opacity-90"
                  data-testid="btn-riprogramma"
                >
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Riprogramma
                </Button>
                <Button
                  type="button"
                  data-state="colored"
                  style={{ backgroundColor: "#dc2626" }}
                  onClick={() => setShowCancella(true)}
                  className="text-white hover:opacity-90"
                  data-testid="btn-cancella"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancella
                </Button>
              </div>
            )}

            {showRipro && (
              <div className="pt-4 border-t space-y-3">
                <h3 className="font-semibold text-slate-900">Riprogramma rilievo</h3>
                <div>
                  <Label className="text-xs">Nuova data</Label>
                  <Input type="date" value={newDate} onChange={(e) => { setNewDate(e.target.value); setSelectedSlot(""); }} min={todayIso()} data-testid="input-newdate" />
                </div>
                {loadingSlots ? (
                  <p className="text-xs italic text-slate-500">Caricamento slot...</p>
                ) : slots.length === 0 ? (
                  <p className="text-xs italic text-slate-500">Nessuno slot disponibile per questo giorno</p>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {slots.map((s) => (
                      <button
                        key={s.orario}
                        type="button"
                        data-state="colored"
                        onClick={() => setSelectedSlot(s.orario)}
                        style={{ backgroundColor: selectedSlot === s.orario ? "#059669" : "#ffffff" }}
                        className={`py-1.5 text-sm font-medium rounded-md border ${selectedSlot === s.orario ? "border-emerald-700 text-white" : "border-slate-300 text-slate-800 hover:bg-emerald-50"}`}
                      >
                        {s.orario}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowRipro(false)} className="flex-1">Annulla</Button>
                  <Button type="button" data-state="colored" style={{ backgroundColor: "#2563eb" }} onClick={handleRiprogramma} disabled={rescheduling || !selectedSlot} className="flex-1 text-white hover:opacity-90" data-testid="btn-conferma-ripro">
                    {rescheduling ? "Attendere..." : "Conferma Riprogrammazione"}
                  </Button>
                </div>
              </div>
            )}

            {showCancella && (
              <div className="pt-4 border-t space-y-3">
                <h3 className="font-semibold text-red-700 text-lg">⚠️ Cancella rilievo</h3>
                <div>
                  <Label className="text-sm font-semibold text-red-700">Motivo cancellazione *</Label>
                  <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} placeholder="Es. imprevisto lavoro, assenza, altro appuntamento..." className="border-red-300 focus:border-red-500" data-testid="input-motivo-cancella" />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowCancella(false)} className="flex-1">Annulla</Button>
                  <Button type="button" data-state="colored" style={{ backgroundColor: "#dc2626" }} onClick={handleCancella} disabled={cancelling} className="flex-1 text-white hover:opacity-90" data-testid="btn-conferma-cancella">
                    {cancelling ? "Attendere..." : "Conferma Cancellazione"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="font-medium text-slate-900 break-words">{value}</p>
      </div>
    </div>
  );
}
